import { useEffect } from 'react';

import SingleSimcard from './SingleSimcard';
import MultiSimcard from './MultiSimcard';
import {
    fetchExtension,
    fetchMobileSubscription,
} from 'flow/actions/extension-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { MobileSubscription } from 'flow/types/Extension';
import { TypedMap } from 'flow/types/TypedMap';
import useAppContext from 'flow/hooks/useAppContext';
import Page from './Page';
import { getAllSimcardsForExtension } from 'flow/selectors/extension-selectors';
import { getUserCountryCode } from 'flow/selectors/selectors';
import { hasAccessToEsimSettings } from 'flow/utils/sim-card-utils';
import RouteLink from '../ui-components/RouteLink';
import { routes } from 'flow/types/routes';
import { useHistory, useParams } from 'react-router-dom';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { generatePath } from 'flow/utils/router-utils';

const Mobile = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { isSinglePaneMode } = useSinglePaneMode();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const allSimcards: TypedMap<MobileSubscription> = useAppSelector((state) =>
        getAllSimcardsForExtension(state, extensionKey),
    );

    const mobileSubscription = useAppSelector((state) =>
        state.extensions.getIn([
            'extensions',
            extensionKey,
            'mobileSubscription',
        ]),
    );

    const hasMobile = useAppSelector(
        (state) =>
            state.extensions.getIn([
                'extensions',
                extensionKey,
                'license',
                'licenseUsageType',
            ]) === 'mobile',
    );
    const userCountryCode = useAppSelector((state) =>
        getUserCountryCode(state, extensionKey).toLowerCase(),
    );

    // Fetch mobile subscription data only if not already loaded
    useEffect(() => {
        if (extensionKey && !mobileSubscription) {
            dispatch(fetchMobileSubscription(extensionKey));
            dispatch(
                fetchExtension(extensionKey, 'all', 'live', 'mno_subscription'),
            );
        }
    }, [dispatch, extensionKey, mobileSubscription]);

    const hasAccessToEsim = hasAccessToEsimSettings(
        allSimcards,
        hasMobile,
        userCountryCode,
    );

    const EmptyView = (
        <Page
            header="settings.mobile"
            onClickBack={
                isSinglePaneMode
                    ? () =>
                          history.push(
                              generatePath(routes['settings'], {
                                  extensionKey,
                              }),
                          )
                    : undefined
            }
        >
            <div className="text-center text-gray-400">
                {i18next.t('settings.no_simcards')}
            </div>
        </Page>
    );

    if (!extensionKey || !allSimcards?.size) {
        if (hasAccessToEsim) {
            return (
                <Page
                    header="settings.mobile"
                    onClickBack={
                        isSinglePaneMode
                            ? () =>
                                  history.push(
                                      generatePath(routes['settings'], {
                                          extensionKey,
                                      }),
                                  )
                            : undefined
                    }
                >
                    <RouteLink to={routes['settings.mobile.esim']}>
                        {i18next.t('settings.esim')}
                    </RouteLink>
                </Page>
            );
        }
        return EmptyView;
    }

    if (mobileSubscription.get('simcards')?.size === 0) {
        return EmptyView;
    }

    const simcard = mobileSubscription
        .get('simcards')
        .find((sc) => sc.get('isMainSimcard'));

    if (mobileSubscription.get('simcards').size > 1) {
        return <MultiSimcard mobileSubscription={mobileSubscription} />;
    }

    return <SingleSimcard defaultSimcard={simcard} />;
};

export default Mobile;
